import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/Layout.jsx';
import {
  Alert, Card, Empty, ErrorState, Input, Loading, NumberInput, Segmented, Select, Stat, Textarea,
} from '../components/ui.jsx';
import { Modal, ConfirmModal } from '../components/Modal.jsx';
import { OpeningBalanceForm } from '../components/OpeningBalanceForm.jsx';
import { IconExchange, IconPlus } from '../components/Icons.jsx';
import { api } from '../lib/api.js';
import { useApi, useDebounced } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { money, moneyPair, num, today } from '../lib/format.js';

const KIND_LABEL = {
  contribution: t.shareholder.contribution,
  payout: t.shareholder.payout,
  adjustment: t.shareholder.adjustment,
};
const KIND_BADGE = { contribution: 'cash', payout: 'debt', adjustment: 'muted' };

export default function Shareholders() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [newTx, setNewTx] = useState(null);
  const [editingTx, setEditingTx] = useState(null);
  const [deletingTx, setDeletingTx] = useState(null);
  const [settingOpening, setSettingOpening] = useState(null);
  const [busy, setBusy] = useState(false);

  const [txShareholder, setTxShareholder] = useState('');
  const [txKind, setTxKind] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const shareholders = useApi('/api/shareholders', { q: debouncedSearch, include_inactive: true });
  const allShareholders = useApi('/api/shareholders', { include_inactive: true });
  const txList = useApi('/api/shareholders/transactions/all', {
    shareholder_id: txShareholder, kind: txKind, from, to, limit: 200,
  });

  const totalEquity = (allShareholders.data || []).reduce(
    (acc, sh) => ({
      iqd: acc.iqd + Number(sh.balance_iqd),
      usd: acc.usd + Number(sh.balance_usd),
    }),
    { iqd: 0, usd: 0 },
  );

  const reloadAll = () => {
    shareholders.reload();
    allShareholders.reload();
    txList.reload();
  };

  const removeTx = async () => {
    setBusy(true);
    try {
      await api.del(`/api/shareholders/transactions/${deletingTx.id}`);
      toast.success(t.shareholder.transactionDeleted);
      setDeletingTx(null);
      reloadAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/api/shareholders/${deleting.id}`);
      toast.success(t.shareholder.deleted);
      setDeleting(null);
      reloadAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t.shareholder.title}
        subtitle={t.shareholder.subtitle}
        actions={
          <div className="btn-row no-print">
            <button type="button" className="btn btn-sm" onClick={() => setEditing({})}>
              <IconPlus /> {t.shareholder.addOne}
            </button>
            <button type="button" className="btn btn-sm btn-primary" onClick={() => setNewTx({})}>
              <IconExchange /> {t.shareholder.newTransaction}
            </button>
          </div>
        }
      />

      <div className="page">
        <div className="stat-grid">
          <Stat label={t.shareholder.totalEquity} value={money(totalEquity.iqd, 'IQD')} hint={money(totalEquity.usd, 'USD')} />
        </div>

        <Card title={t.shareholder.title}>
          <Input
            label={t.common.search}
            placeholder={t.shareholder.searchHint}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <p className="small muted mt-0">{t.shareholder.accountNote}</p>

          <ErrorState error={shareholders.error} onRetry={shareholders.reload} />
          {shareholders.loading && !shareholders.data && <Loading />}

          {shareholders.data && shareholders.data.length === 0 && (
            <Empty
              title={t.shareholder.emptyTitle}
              message={t.shareholder.emptyHint}
              action={
                <button type="button" className="btn btn-primary" onClick={() => setEditing({})}>
                  <IconPlus /> {t.shareholder.addOne}
                </button>
              }
            />
          )}

          {shareholders.data && shareholders.data.length > 0 && (
            <div className="record-list">
              {shareholders.data.map((sh) => {
                const owed = Number(sh.balance_iqd) > 0 || Number(sh.balance_usd) > 0;
                return (
                  <div key={sh.id} className="record">
                    <div className="record-top">
                      <Link className="title" to={`/shareholders/${sh.id}`}>
                        {sh.name}
                      </Link>
                      <span className={`amount ${owed ? 'neg' : 'pos'}`}>{money(sh.balance_iqd, 'IQD')}</span>
                    </div>
                    <div className="record-meta">
                      {sh.phone && <span>{sh.phone}</span>}
                      {Number(sh.balance_usd) !== 0 && (
                        <span className={Number(sh.balance_usd) > 0 ? 'neg' : 'pos'}>{money(sh.balance_usd, 'USD')}</span>
                      )}
                      <span className="badge muted">{t.shareholder.cutPercent}: {sh.cut_percent}٪</span>
                      {!owed && <span className="badge muted">{t.shareholder.settled}</span>}
                      {!sh.active && <span className="badge muted">{t.common.inactive}</span>}
                    </div>
                    <div className="btn-row no-print" style={{ marginTop: 4 }}>
                      <button type="button" className="btn btn-sm" onClick={() => setNewTx({ shareholder: sh })}>
                        <IconExchange /> {t.shareholder.newTransaction}
                      </button>
                      <button type="button" className="btn btn-sm" onClick={() => navigate(`/shareholders/${sh.id}`)}>
                        {t.shareholder.statement}
                      </button>
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(sh)}>
                        {t.common.edit}
                      </button>
                      {isAdmin && (
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setSettingOpening(sh)}>
                          {t.openingBalance.action}
                        </button>
                      )}
                      {isAdmin && (
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeleting(sh)}>
                          {t.common.delete}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card title={t.shareholder.recentTransactions}>
          <div className="filters">
            <Select
              label={t.shareholder.title}
              placeholder={t.shareholder.allShareholders}
              value={txShareholder}
              onChange={(e) => setTxShareholder(e.target.value)}
              options={(allShareholders.data || []).map((sh) => ({ value: sh.id, label: sh.name }))}
            />
            <Select
              label={t.shareholder.kind}
              placeholder={t.shareholder.allKinds}
              value={txKind}
              onChange={(e) => setTxKind(e.target.value)}
              options={Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label }))}
            />
            <Input label={t.common.from} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Input label={t.common.to} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>

          {txList.data?.totals && (
            <div className="stat-grid">
              <Stat label={t.exchange.entryCount} value={num(txList.data.totals.count)} />
              <Stat
                label={t.shareholder.totalContributions}
                value={moneyPair(txList.data.totals.contributions_iqd, txList.data.totals.contributions_usd, { zero: '-' })}
              />
              <Stat
                label={t.shareholder.totalPayouts}
                value={moneyPair(txList.data.totals.payouts_iqd, txList.data.totals.payouts_usd, { zero: '-' })}
              />
            </div>
          )}

          <ErrorState error={txList.error} onRetry={txList.reload} />
          {txList.loading && !txList.data && <Loading />}
          {txList.data && txList.data.rows.length === 0 && <Empty title={t.shareholder.noTransactions} />}

          {txList.data && txList.data.rows.length > 0 && (
            <div className="record-list">
              {txList.data.rows.map((row) => (
                <div key={row.id} className="record">
                  <div className="record-top">
                    <span className={`badge ${KIND_BADGE[row.kind]}`}>{KIND_LABEL[row.kind]}</span>
                    <span className="title">{row.shareholder_name}</span>
                    <span className="amount">{money(row.amount, row.currency)}</span>
                  </div>
                  <div className="record-meta">
                    <span>{row.happened_on}</span>
                    {row.reference && <span>{row.reference}</span>}
                  </div>
                  <div className="btn-row no-print" style={{ marginTop: 4 }}>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditingTx(row)}>
                      {t.common.edit}
                    </button>
                    {isAdmin && (
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeletingTx(row)}>
                        {t.common.delete}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {editing && (
        <ShareholderForm
          shareholder={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            toast.success(t.shareholder.saved(saved.name));
            reloadAll();
          }}
        />
      )}

      {newTx && (
        <TransactionForm
          shareholders={allShareholders.data || []}
          initialShareholderId={newTx.shareholder?.id}
          onClose={() => setNewTx(null)}
          onSaved={() => {
            setNewTx(null);
            toast.success(t.shareholder.transactionSaved);
            reloadAll();
          }}
        />
      )}

      {editingTx && (
        <TransactionForm
          shareholders={allShareholders.data || []}
          transaction={editingTx}
          onClose={() => setEditingTx(null)}
          onSaved={() => {
            setEditingTx(null);
            toast.success(t.shareholder.transactionSaved);
            reloadAll();
          }}
        />
      )}

      {deletingTx && (
        <ConfirmModal
          title={t.shareholder.deleteTransaction}
          message={t.shareholder.deleteTransactionMessage}
          onConfirm={removeTx}
          onClose={() => setDeletingTx(null)}
          busy={busy}
        />
      )}

      {deleting && (
        <ConfirmModal
          title={t.shareholder.deleteTitle}
          message={t.shareholder.deleteMessage(deleting.name)}
          onConfirm={remove}
          onClose={() => setDeleting(null)}
          busy={busy}
        />
      )}

      {settingOpening && (
        <OpeningBalanceForm
          partyType="shareholder"
          partyId={settingOpening.id}
          partyName={settingOpening.name}
          onClose={() => setSettingOpening(null)}
          onSaved={() => {
            setSettingOpening(null);
            reloadAll();
          }}
        />
      )}
    </>
  );
}

function ShareholderForm({ shareholder, onClose, onSaved }) {
  const isEdit = Boolean(shareholder.id);
  const [form, setForm] = useState({
    name: shareholder.name || '',
    phone: shareholder.phone || '',
    cut_percent: shareholder.cut_percent ?? '',
    notes: shareholder.notes || '',
    active: shareholder.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = isEdit
        ? await api.patch(`/api/shareholders/${shareholder.id}`, form)
        : await api.post('/api/shareholders', form);
      onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isEdit ? t.shareholder.editTitle : t.shareholder.newTitle}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !form.name.trim()}>
            {busy ? <span className="spinner" /> : t.common.save}
          </button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      <Input label={t.common.name} required value={form.name} onChange={(e) => set({ name: e.target.value })} />
      <Input
        label={t.common.phone}
        value={form.phone}
        onChange={(e) => set({ phone: e.target.value })}
        inputMode="tel"
        dir="ltr"
      />
      <NumberInput
        label={t.shareholder.cutPercent}
        value={form.cut_percent}
        onChange={(cut_percent) => set({ cut_percent })}
        hint={t.shareholder.cutPercentHint}
      />
      <Textarea label={t.common.notes} value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} />
      {isEdit && (
        <Select
          label={t.common.status}
          value={form.active ? 'true' : 'false'}
          onChange={(e) => set({ active: e.target.value === 'true' })}
          options={[
            { value: 'true', label: t.common.active },
            { value: 'false', label: t.common.inactive },
          ]}
        />
      )}
    </Modal>
  );
}

export function TransactionForm({ shareholders, transaction, initialShareholderId, onClose, onSaved }) {
  const isEdit = Boolean(transaction);
  const kind = transaction?.kind;

  const [form, setForm] = useState({
    shareholder_id: transaction?.shareholder_id ?? initialShareholderId ?? '',
    kind: kind || 'contribution',
    happened_on: transaction?.happened_on || today(),
    amount: transaction?.amount || '',
    currency: transaction?.currency || 'IQD',
    method: transaction?.method || 'cash',
    reference: transaction?.reference || '',
    notes: transaction?.notes || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const effectiveKind = isEdit ? kind : form.kind;
  const isAdjustment = effectiveKind === 'adjustment';

  const canSave = form.shareholder_id && form.happened_on && form.currency
    && (isAdjustment ? Number(form.amount) !== 0 : Number(form.amount) > 0);

  const save = async () => {
    setBusy(true);
    setError(null);
    const payload = {
      shareholder_id: Number(form.shareholder_id),
      happened_on: form.happened_on,
      amount: form.amount,
      currency: form.currency,
      method: form.method,
      reference: form.reference,
      notes: form.notes,
    };
    if (!isEdit) payload.kind = form.kind;

    try {
      if (isEdit) await api.patch(`/api/shareholders/transactions/${transaction.id}`, payload);
      else {
        await api.post('/api/shareholders/transactions', payload, {
          queueOffline: true,
          label: `${t.shareholder.title} — ${KIND_LABEL[form.kind]}`,
        });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isEdit ? t.shareholder.editTransaction : t.shareholder.newTransaction}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !canSave}>
            {busy ? <span className="spinner" /> : t.common.save}
          </button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}

      <Select
        label={t.shareholder.title}
        required
        placeholder={t.shareholder.allShareholders}
        value={form.shareholder_id}
        onChange={(e) => set({ shareholder_id: e.target.value })}
        options={shareholders.map((sh) => ({ value: sh.id, label: sh.name }))}
        disabled={isEdit}
      />

      {isEdit ? (
        <Alert tone="info">
          {KIND_LABEL[kind]} — {t.exchange.kindLocked}
        </Alert>
      ) : (
        <Segmented
          label={t.shareholder.kind}
          value={form.kind}
          onChange={(v) => set({ kind: v })}
          options={Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label }))}
          hint={{
            contribution: t.shareholder.contributionHint,
            payout: t.shareholder.payoutHint,
            adjustment: t.shareholder.adjustmentHint,
          }[form.kind]}
        />
      )}

      <Input
        label={t.common.date}
        required
        type="date"
        value={form.happened_on}
        onChange={(e) => set({ happened_on: e.target.value })}
      />

      <div className="form-grid cols-2">
        <NumberInput
          label={t.common.amount}
          required
          allowNegative={isAdjustment}
          value={form.amount}
          onChange={(amount) => set({ amount })}
          hint={isAdjustment ? t.shareholder.adjustmentHint : undefined}
        />
        <Segmented
          label={t.common.currency}
          value={form.currency}
          onChange={(currency) => set({ currency })}
          options={[
            { value: 'IQD', label: t.report.iqd },
            { value: 'USD', label: t.report.usd },
          ]}
        />
      </div>

      <Segmented
        label={t.shareholder.paymentType}
        value={form.method}
        onChange={(method) => set({ method })}
        options={[{ value: 'cash', label: t.shareholder.paymentCash }]}
      />

      <Input label={t.common.reference} value={form.reference} onChange={(e) => set({ reference: e.target.value })} />
      <Textarea label={t.common.notes} value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} />
    </Modal>
  );
}
